import {
  ChevronRight,
  Keyboard,
  LogOut,
  Menu as MenuIcon,
  Monitor,
  Moon,
  RefreshCw,
  Search,
  Settings,
  Sparkles,
  Sun,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { cn } from '../../../lib/cn';
import { displayName, type AuthUser } from '../../../lib/auth';
import {
  Avatar,
  Button,
  Hint,
  IconButton,
  Kbd,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '../../../ui';
import { SuiteLauncher } from '@vaarta/suite-launcher';

import { useAuth } from '../../../hooks/useAuth';
import { crumbsFor } from './nav';
import { OrganizationSwitcher } from './OrganizationSwitcher';
import { usePrefs, type ThemeChoice } from './prefs';

export interface TopBarProps {
  user: AuthUser | null;
  onOpenNav: () => void;
  onOpenSearch: () => void;
  onOpenShortcuts: () => void;
  onToggleAssistant: () => void;
  assistantOpen: boolean;
  onRefresh: () => void;
  refreshing: boolean;
  onSignOut: () => void;
  /** Resolved names for id segments, so the breadcrumb reads like the product. */
  names: { chart?: string; dashboard?: string };
}

const THEME_OPTIONS: Array<{ value: ThemeChoice; label: string; icon: typeof Sun }> = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'Match system', icon: Monitor },
];

export function TopBar({
  user,
  onOpenNav,
  onOpenSearch,
  onOpenShortcuts,
  onToggleAssistant,
  assistantOpen,
  onRefresh,
  refreshing,
  onSignOut,
  names,
}: TopBarProps) {
  const { pathname } = useLocation();
  const { config } = useAuth();
  const { prefs, set } = usePrefs();
  const crumbs = crumbsFor(pathname, names);
  const name = displayName(user);

  return (
    <header className="flex h-13 shrink-0 items-center gap-2 border-b border-border bg-surface px-3 lg:px-gutter">
      <IconButton label="Open navigation" className="lg:hidden" onClick={onOpenNav}>
        <MenuIcon />
      </IconButton>

      <nav aria-label="Breadcrumb" className="flex min-w-0 flex-1 items-center gap-1 text-[0.8125rem]">
        {crumbs.map((crumb, i) => {
          const last = i === crumbs.length - 1;
          return (
            <span key={`${crumb.label}-${i}`} className="flex min-w-0 items-center gap-1">
              {i > 0 && <ChevronRight className="size-3.5 shrink-0 text-fg-faint" aria-hidden />}
              {crumb.to && !last ? (
                <Link
                  to={crumb.to}
                  className="truncate rounded-xs text-fg-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span
                  aria-current={last ? 'page' : undefined}
                  className={cn('truncate', last ? 'font-medium text-fg' : 'text-fg-muted')}
                >
                  {crumb.label}
                </span>
              )}
            </span>
          );
        })}
      </nav>

      {/* Search: a real field where there is room, a button where there is not. */}
      <button
        type="button"
        onClick={onOpenSearch}
        className={cn(
          'hidden h-8 w-56 items-center gap-2 rounded-md border border-border bg-surface-subtle px-2.5 text-left text-xs text-fg-muted transition-colors',
          'hover:border-border-strong hover:text-fg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus md:flex',
        )}
      >
        <Search className="size-3.5 shrink-0" aria-hidden />
        <span className="flex-1 truncate">Search…</span>
        <Kbd>⌘K</Kbd>
      </button>
      <IconButton label="Search" className="md:hidden" onClick={onOpenSearch}>
        <Search />
      </IconButton>

      {/* AG-1543 — the shared suite launcher, `@vaarta/suite-launcher`, which replaced this
          application's own 118-line copy of it. Three other products had their own; the
          package is the only one now, and no SPA builds its own application list.
          ⚠️ `launcherUrl` comes from the backend and is never assembled here: the suite
          cookie carries no `Domain` attribute, so the same path on the API host answers
          "no shared session" forever. Absent on a deployment that does not authenticate,
          and the component then renders nothing. */}
      <SuiteLauncher launcherUrl={config?.launcherUrl} currentAppName="Analytics" />
      {/* AG-2415 — change organisation without signing out. Draws nothing unless this
          person holds two or more organisations here. */}
      <OrganizationSwitcher />

      <Hint content="Reload charts, dashboards and data sources" side="bottom">
        <IconButton label="Refresh workspace data" onClick={onRefresh} disabled={refreshing}>
          <RefreshCw className={refreshing ? 'animate-spin' : undefined} />
        </IconButton>
      </Hint>

      <Hint content="Assistant" shortcut="⌘J" side="bottom">
        <Button
          variant={assistantOpen ? 'subtle' : 'secondary'}
          size="md"
          icon={<Sparkles />}
          onClick={onToggleAssistant}
          aria-pressed={assistantOpen}
          className="hidden sm:inline-flex"
        >
          Assistant
        </Button>
      </Hint>
      <IconButton
        label="Assistant"
        className="sm:hidden"
        active={assistantOpen}
        onClick={onToggleAssistant}
      >
        <Sparkles />
      </IconButton>

      <Menu>
        <MenuTrigger asChild>
          <button
            type="button"
            className="rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
            aria-label="Account menu"
          >
            <Avatar name={name} />
          </button>
        </MenuTrigger>
        <MenuContent className="w-60">
          {/* Whose workspace this is — the owner the backend adopted from the
              data, not a signed-in identity. Nothing here was authenticated. */}
          <div className="px-2 py-1.5">
            <p className="truncate text-[0.8125rem] font-medium text-fg">
              {user?.name || 'Workspace'}
            </p>
            <p className="truncate text-xs text-fg-muted">{user?.email}</p>
          </div>
          <MenuSeparator />
          <MenuLabel>Appearance</MenuLabel>
          <MenuRadioGroup value={prefs.theme} onValueChange={(v) => set('theme', v as ThemeChoice)}>
            {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
              <MenuRadioItem key={value} value={value}>
                <Icon />
                {label}
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
          <MenuSeparator />
          <MenuItem asChild>
            <Link to="/settings">
              <Settings />
              Settings
            </Link>
          </MenuItem>
          <MenuItem onSelect={onOpenShortcuts} shortcut="?">
            <Keyboard />
            Keyboard shortcuts
          </MenuItem>
          <MenuSeparator />
          <MenuItem destructive onSelect={onSignOut}>
            <LogOut />
            Sign out
          </MenuItem>
        </MenuContent>
      </Menu>
    </header>
  );
}
